import {
  AddActionData,
  ArticleStepData,
  AttachmentAnalysisUploadStepData,
  ButtonsStepData,
  CodeBlockStepData,
  ConditionStepData,
  CsatTriggerPointStepData,
  EmbedStepData,
  FormStepData,
  GoToComponentStepData,
  GoToIntentData,
  ImageStepData,
  MiniAutoflowPolicyStepData,
  RestartStepData,
  SpeechStepData,
  StepUpdateData,
  TextMessageData,
} from '../../slices/canvas-workflow-builder/types/dataTypes';
import { SOLVE_V2_BASE_URL } from '../constants';
import { dashboardApi } from '../dashboard-api';
import {
  getEditingSavedVersion,
  getEditingSavedVersionQuery,
} from '../editingSavedVersion';
import { workflowBuilderAutoflowApi } from '../workflowBuilderAutoflowApi.ts/workflowBuilderAutoflowApi';
import { WorkflowVersionType } from 'src/pages/workflow-builder-edit/autonomous-agent/constants';
import { SolveWidgetProduct } from 'src/types/types';
import {
  ActionSettingsCustomization,
  CanvasResponse,
  WorkflowVersionsResponse,
} from 'src/types/workflowBuilderAPITypes';
import { StepTypes } from 'src/utils/enums';

const BASE_URL = `${SOLVE_V2_BASE_URL}/workflow-builder`;

const stepInvalidatesIntentUsages = (stepType?: StepTypes) =>
  stepType === StepTypes.GO_TO_COMPONENT || stepType === StepTypes.GO_TO_INTENT;

const getWorkflowInvalidationTags = (
  intentWorkflowId: string,
  invalidatesIntentUsages = false,
) => [
  { id: intentWorkflowId, type: 'Workflow' as const },
  ...(invalidatesIntentUsages ? (['IntentUsages'] as const) : []),
];

/**
 * Helper to update the parent autoflow's unsaved_changes state in the cache.
 * This is used when a child workflow (building block) is modified.
 */
const updateParentAutoflowUnsavedState = (
  dispatch: (
    action: ReturnType<typeof workflowBuilderAutoflowApi.util.updateQueryData>,
  ) => void,
  parentWorkflowId: string,
) => {
  dispatch(
    workflowBuilderAutoflowApi.util.updateQueryData(
      'getAutoflowWorkflow',
      parentWorkflowId,
      draft => {
        draft.unsaved_changes = true;
      },
    ),
  );
};

const workflowApi = dashboardApi.injectEndpoints({
  endpoints: builder => ({
    addActionToWorkflow: builder.mutation<
      CanvasResponse,
      {
        actionData: AddActionData;
        intentWorkflowId: string;
        parentWorkflowId?: string;
        version: number;
      }
    >({
      invalidatesTags: (_result, _error, { intentWorkflowId }) =>
        getWorkflowInvalidationTags(intentWorkflowId),
      async onQueryStarted(
        { intentWorkflowId, parentWorkflowId },
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          // Immediately update the cache with the server response
          dispatch(
            workflowApi.util.updateQueryData(
              'loadDraftWorkflow',
              { intentWorkflowId },
              draft => {
                Object.assign(draft, data);
              },
            ),
          );
          // Update parent autoflow's unsaved state if this is a building block
          if (parentWorkflowId) {
            updateParentAutoflowUnsavedState(dispatch, parentWorkflowId);
          }
        } catch {
          // Error handled by caller
        }
      },
      query: ({ actionData, intentWorkflowId, version }) => ({
        body: {
          ...actionData,
          editing_saved_version: getEditingSavedVersion(),
        },
        method: 'POST',
        url: `${BASE_URL}/${intentWorkflowId}/${version}/action`,
      }),
    }),
    addStepToWorkflow: builder.mutation<
      CanvasResponse,
      {
        intentWorkflowId: string;
        parentWorkflowId?: string;
        stepData:
          | ArticleStepData
          | TextMessageData
          | ButtonsStepData
          | FormStepData
          | GoToComponentStepData
          | GoToIntentData
          | RestartStepData
          | ConditionStepData
          | ImageStepData
          | EmbedStepData
          | CodeBlockStepData
          | MiniAutoflowPolicyStepData
          | CsatTriggerPointStepData
          | AttachmentAnalysisUploadStepData
          | SpeechStepData;
        version: number;
      }
    >({
      invalidatesTags: (_result, _error, { intentWorkflowId, stepData }) =>
        getWorkflowInvalidationTags(
          intentWorkflowId,
          stepInvalidatesIntentUsages(stepData.step_type),
        ),
      async onQueryStarted(
        { intentWorkflowId, parentWorkflowId },
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          // Immediately update the cache with the server response;
          dispatch(
            workflowApi.util.updateQueryData(
              'loadDraftWorkflow',
              { intentWorkflowId },
              draft => {
                Object.assign(draft, data);
              },
            ),
          );
          // Update parent autoflow's unsaved state if this is a building block
          if (parentWorkflowId) {
            updateParentAutoflowUnsavedState(dispatch, parentWorkflowId);
          }
        } catch {
          // Error handled by caller
        }
      },
      query: ({ intentWorkflowId, stepData, version }) => ({
        body: {
          ...stepData,
          editing_saved_version: getEditingSavedVersion(),
        },
        method: 'POST',
        url: `${BASE_URL}/${intentWorkflowId}/${version}/step`,
      }),
    }),
    deleteActionFromWorkflow: builder.mutation<
      CanvasResponse,
      {
        canvasActionId: string;
        intentWorkflowId: string;
        parentWorkflowId?: string;
        version: number;
      }
    >({
      invalidatesTags: (_result, _error, { intentWorkflowId }) =>
        getWorkflowInvalidationTags(intentWorkflowId),
      async onQueryStarted(
        { intentWorkflowId, parentWorkflowId },
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          // Immediately update the cache with the server response
          dispatch(
            workflowApi.util.updateQueryData(
              'loadDraftWorkflow',
              { intentWorkflowId },
              draft => {
                Object.assign(draft, data);
              },
            ),
          );
          // Update parent autoflow's unsaved state if this is a building block
          if (parentWorkflowId) {
            updateParentAutoflowUnsavedState(dispatch, parentWorkflowId);
          }
        } catch {
          // Error handled by caller
        }
      },
      query: ({ canvasActionId, intentWorkflowId, version }) => ({
        method: 'DELETE',
        url: `${BASE_URL}/${intentWorkflowId}/${version}/action/${canvasActionId}${getEditingSavedVersionQuery()}`,
      }),
    }),
    deleteStepAndBelowFromWorkflow: builder.mutation<
      CanvasResponse,
      {
        canvasActionId?: string;
        intentWorkflowId: string;
        parentWorkflowId?: string;
        stepId?: string;
        version: number;
      }
    >({
      invalidatesTags: (_result, _error, { intentWorkflowId }) =>
        getWorkflowInvalidationTags(intentWorkflowId, true),
      async onQueryStarted(
        { intentWorkflowId, parentWorkflowId },
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          // Immediately update the cache with the server response
          dispatch(
            workflowApi.util.updateQueryData(
              'loadDraftWorkflow',
              { intentWorkflowId },
              draft => {
                Object.assign(draft, data);
              },
            ),
          );
          // Update parent autoflow's unsaved state if this is a building block
          if (parentWorkflowId) {
            updateParentAutoflowUnsavedState(dispatch, parentWorkflowId);
          }
        } catch {
          // Error handled by caller
        }
      },
      query: ({ canvasActionId, intentWorkflowId, stepId, version }) => ({
        body: {
          ...(stepId
            ? { step_id: stepId }
            : { canvas_action_id: canvasActionId }),
          editing_saved_version: getEditingSavedVersion(),
        },
        method: 'PUT',
        url: `${BASE_URL}/${intentWorkflowId}/${version}/delete-step-and-subsequent-steps`,
      }),
    }),
    deleteStepFromWorkflow: builder.mutation<
      CanvasResponse,
      {
        intentWorkflowId: string;
        parentWorkflowId?: string;
        stepId: string;
        version: number;
      }
    >({
      invalidatesTags: (_result, _error, { intentWorkflowId }) =>
        getWorkflowInvalidationTags(intentWorkflowId, true),
      async onQueryStarted(
        { intentWorkflowId, parentWorkflowId },
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          // Immediately update the cache with the server response
          dispatch(
            workflowApi.util.updateQueryData(
              'loadDraftWorkflow',
              { intentWorkflowId },
              draft => {
                Object.assign(draft, data);
              },
            ),
          );
          // Update parent autoflow's unsaved state if this is a building block
          if (parentWorkflowId) {
            updateParentAutoflowUnsavedState(dispatch, parentWorkflowId);
          }
        } catch {
          // Error handled by caller
        }
      },
      query: ({ intentWorkflowId, stepId, version }) => ({
        method: 'DELETE',
        url: `${BASE_URL}/${intentWorkflowId}/${version}/step/${stepId}${getEditingSavedVersionQuery()}`,
      }),
    }),
    getParentWorkflowId: builder.query<
      { parent_autoflow_intent_workflow_id: string | null },
      { intentWorkflowId: string }
    >({
      query: ({ intentWorkflowId }) =>
        `${BASE_URL}/workflow/${intentWorkflowId}/parent`,
    }),
    getWorkflowIdFromIntent: builder.query<
      { intent_workflow_id: string },
      { intentId: string; product: SolveWidgetProduct }
    >({
      query: ({ intentId, product }) =>
        `${BASE_URL}/intent/${intentId}/workflow/${product}`,
    }),
    loadActionSettings: builder.query<
      ActionSettingsCustomization,
      {
        canvasActionId: string;
        intentWorkflowId: string;
        version: number;
      }
    >({
      providesTags: (_result, _error, { canvasActionId, version }) => [
        { id: `${canvasActionId}-${version}`, type: 'ActionSettings' },
      ],
      query: ({ canvasActionId, intentWorkflowId, version }) => ({
        method: 'GET',
        url: `${BASE_URL}/${intentWorkflowId}/${version}/action/${canvasActionId}`,
      }),
    }),
    loadDefaultActionSettings: builder.query<
      ActionSettingsCustomization['configuration_fields'],
      {
        actionType: string;
        connectorId?: string;
        intentWorkflowId: string;
      }
    >({
      query: ({ actionType, connectorId, intentWorkflowId }) => {
        let url = `${SOLVE_V2_BASE_URL}/workflow-builder/actions/default?workflow_action_type=${actionType}`;
        if (connectorId) {
          url += `&connector_id=${encodeURIComponent(connectorId)}`;
        }
        return {
          headers: {
            workflow_id: intentWorkflowId,
          },
          method: 'GET',
          url,
        };
      },
    }),
    loadDraftWorkflow: builder.query<
      CanvasResponse,
      {
        intentWorkflowId: string;
        parentAutoflowVersion?: number;
        parentAutoflowVersionType?: WorkflowVersionType;
      }
    >({
      // serializeQueryArgs collapses the cache key to { intentWorkflowId },
      // but the parent version params are baked into the request URL and the
      // server classifies child CVs (hard-undefined vs parent-provided)
      // against THAT parent version. Without this, a response fetched during
      // a version-switch transition (wrong/absent parent args) sticks in the
      // shared entry forever — RTK never refetches on arg change when the
      // serialized key is unchanged.
      forceRefetch: ({ currentArg, previousArg }) =>
        currentArg?.parentAutoflowVersion !==
          previousArg?.parentAutoflowVersion ||
        currentArg?.parentAutoflowVersionType !==
          previousArg?.parentAutoflowVersionType,
      providesTags: (_result, _error, { intentWorkflowId }) => [
        { id: intentWorkflowId, type: 'Workflow' },
      ],
      query: ({
        intentWorkflowId,
        parentAutoflowVersion,
        parentAutoflowVersionType,
      }) => {
        const params = new URLSearchParams();
        if (parentAutoflowVersion !== undefined) {
          params.set('parent_autoflow_version', String(parentAutoflowVersion));
        }
        if (parentAutoflowVersionType) {
          params.set('parent_autoflow_version_type', parentAutoflowVersionType);
        }
        const queryString = params.toString();
        return `${BASE_URL}/${intentWorkflowId}${queryString ? `?${queryString}` : ''}`;
      },
      serializeQueryArgs: ({ queryArgs }) => ({
        intentWorkflowId: queryArgs.intentWorkflowId,
      }),
    }),
    loadWorkflowVersion: builder.query<
      CanvasResponse,
      { intentWorkflowId: string; version: number }
    >({
      providesTags: (_result, _error, { intentWorkflowId }) => [
        { id: intentWorkflowId, type: 'Workflow' },
      ],
      query: ({ intentWorkflowId, version }) =>
        `${BASE_URL}/${intentWorkflowId}/${version}`,
    }),
    loadWorkflowVersions: builder.query<
      WorkflowVersionsResponse,
      { intentWorkflowId: string }
    >({
      query: ({ intentWorkflowId }) =>
        `${BASE_URL}/${intentWorkflowId}/versions/latest`,
    }),
    saveWorkflow: builder.mutation<
      CanvasResponse,
      { intentWorkflowId: string; version: number }
    >({
      invalidatesTags: (_result, _error, { intentWorkflowId }) => [
        { id: intentWorkflowId, type: 'Workflow' },
      ],
      query: ({ intentWorkflowId, version }) => ({
        body: {},
        method: 'POST',
        url: `${BASE_URL}/${intentWorkflowId}/${version}/save`,
      }),
    }),
    updateActionInWorkflow: builder.mutation<
      CanvasResponse,
      {
        actionData: ActionSettingsCustomization & {
          is_entry_step: boolean;
          parent_step_id: string | null;
        };
        canvasActionId: string;
        intentWorkflowId: string;
        parentWorkflowId?: string;
        version: number;
      }
    >({
      invalidatesTags: (
        _result,
        _error,
        { canvasActionId, intentWorkflowId, version },
      ) => [
        { id: intentWorkflowId, type: 'Workflow' },
        { id: `${canvasActionId}-${version}`, type: 'ActionSettings' },
      ],
      async onQueryStarted(
        { intentWorkflowId, parentWorkflowId },
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          // Immediately update the cache with the server response
          dispatch(
            workflowApi.util.updateQueryData(
              'loadDraftWorkflow',
              { intentWorkflowId },
              draft => {
                Object.assign(draft, data);
              },
            ),
          );
          // Update parent autoflow's unsaved state if this is a building block
          if (parentWorkflowId) {
            updateParentAutoflowUnsavedState(dispatch, parentWorkflowId);
          }
        } catch {
          // Error handled by caller
        }
      },
      query: ({ actionData, canvasActionId, intentWorkflowId, version }) => ({
        body: {
          ...actionData,
          editing_saved_version: getEditingSavedVersion(),
        },
        method: 'PUT',
        url: `${BASE_URL}/${intentWorkflowId}/${version}/action/${canvasActionId}`,
      }),
    }),
    updateStepInWorkflow: builder.mutation<
      CanvasResponse,
      {
        intentWorkflowId: string;
        invalidatesIntentUsages?: boolean;
        parentWorkflowId?: string;
        stepData: StepUpdateData;
        stepId: string;
        version: number;
      }
    >({
      invalidatesTags: (
        _result,
        _error,
        { intentWorkflowId, invalidatesIntentUsages },
      ) =>
        getWorkflowInvalidationTags(intentWorkflowId, invalidatesIntentUsages),
      async onQueryStarted(
        { intentWorkflowId, parentWorkflowId },
        { dispatch, queryFulfilled },
      ) {
        try {
          const { data } = await queryFulfilled;
          // Immediately update the cache with the server response
          dispatch(
            workflowApi.util.updateQueryData(
              'loadDraftWorkflow',
              { intentWorkflowId },
              draft => {
                Object.assign(draft, data);
              },
            ),
          );
          // Update parent autoflow's unsaved state if this is a building block
          if (parentWorkflowId) {
            updateParentAutoflowUnsavedState(dispatch, parentWorkflowId);
          }
        } catch {
          // Error handled by caller
        }
      },
      query: ({ intentWorkflowId, stepData, stepId, version }) => ({
        body: {
          ...stepData,
          condition_name: stepData.condition_name || null,
          editing_saved_version: getEditingSavedVersion(),
        },
        method: 'PUT',
        url: `${BASE_URL}/${intentWorkflowId}/${version}/step/${stepId}`,
      }),
    }),
  }),
});

export const {
  useAddActionToWorkflowMutation,
  useAddStepToWorkflowMutation,
  useDeleteActionFromWorkflowMutation,
  useDeleteStepAndBelowFromWorkflowMutation,
  useDeleteStepFromWorkflowMutation,
  useGetParentWorkflowIdQuery,
  useGetWorkflowIdFromIntentQuery,
  useLoadActionSettingsQuery,
  useLoadDefaultActionSettingsQuery,
  useLoadDraftWorkflowQuery,
  useLoadWorkflowVersionQuery,
  useLoadWorkflowVersionsQuery,
  useSaveWorkflowMutation,
  useUpdateActionInWorkflowMutation,
  useUpdateStepInWorkflowMutation,
} = workflowApi;

export { workflowApi };
